import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

interface EmptyStateProps {
  /** A lucide icon, drawn large and grey above the title. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** A button that gets the user out of the empty state ("Create list"). */
  action?: ReactNode;
  /**
   * `panel` draws its own outlined box (in place of a page's content);
   * `inline` sits inside a box that's already there (a `Panel`, a table).
   */
  variant?: "panel" | "inline";
}

/** Nothing to show yet, and what to do about it. */
export default function EmptyState({ icon, title, description, action, variant = "inline" }: EmptyStateProps) {
  const content = (
    <Box sx={{ textAlign: "center", py: variant === "panel" ? 0 : 4, px: 2 }}>
      {icon && (
        <Box sx={{ color: "text.disabled", mb: 1, "& svg": { fontSize: 32 } }}>{icon}</Box>
      )}
      <Typography variant="h6" component="p" sx={{ mb: description || action ? 0.5 : 0 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" component="div" sx={{ color: "text.secondary", maxWidth: 480, mx: "auto" }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
  if (variant === "inline") return content;
  return (
    <Paper variant="outlined" sx={{ p: 4 }}>
      {content}
    </Paper>
  );
}
